import { useState } from "react";
import {
  parseDimensionInput,
  validateDimension,
  validateQuantity,
} from "../../../../packages/pvc-product/configurator-controller";
import { formatMoney } from "../../../../packages/pvc-product/pvc-product-page-view-model";
import { PVC_SIGN_ROUTE_PATH } from "../../../../packages/pvc-product/pvc-sign-route";
import { PVC_STOREFRONT_SCHEMA } from "../pvc-runtime";
import { Icon } from "./homepage-icons";
import { useHomepagePvcQuote } from "./use-homepage-pvc-quote";

const { constraints } = PVC_STOREFRONT_SCHEMA;

export function PvcQuickOrder() {
  const [widthInput, setWidthInput] = useState("60");
  const [heightInput, setHeightInput] = useState("40");
  const [quantityInput, setQuantityInput] = useState("1");
  const width = parseDimensionInput(widthInput);
  const height = parseDimensionInput(heightInput);
  const quantity = Number(quantityInput);
  const widthError = validateDimension(width, {
    min: constraints.minWidthCm,
    max: constraints.maxWidthCm,
    step: constraints.stepCm,
  });
  const heightError = validateDimension(height, {
    min: constraints.minHeightCm,
    max: constraints.maxHeightCm,
    step: constraints.stepCm,
  });
  const quantityError = validateQuantity(quantity, constraints);
  const isValid =
    !widthError &&
    !heightError &&
    !quantityError &&
    width !== null &&
    height !== null;
  const { state, retry } = useHomepagePvcQuote(
    width,
    height,
    quantity,
    isValid,
  );
  const quote = state.status === "ready" ? state.quote : null;
  const hasError =
    state.status === "error" ||
    state.status === "stale" ||
    state.status === "rejected";
  const loading = isValid && !quote && !hasError;
  const continueHref = isValid
    ? `${PVC_SIGN_ROUTE_PATH}?width=${width}&height=${height}&quantity=${quantity}`
    : undefined;
  const readyDate = quote
    ? new Date(quote.productionEstimate.earliestReadyDate)
    : null;
  const previewWidth = isValid ? width! : 60;
  const previewHeight = isValid ? height! : 40;
  const previewRatio = previewWidth / previewHeight;
  const previewStyle =
    previewRatio >= 1
      ? { width: "100%", height: `${Math.min(100, 100 / previewRatio)}%` }
      : { height: "100%", width: `${Math.min(100, 100 * previewRatio)}%` };
  const showPreviewCopy = previewRatio >= 0.45 && previewRatio <= 2.8;
  const dimensionDescription = `מידה בס״מ: ${constraints.minWidthCm} עד ${constraints.maxWidthCm}, במספרים שלמים.`;
  const fields = [
    {
      id: "width",
      label: "רוחב (ס״מ)",
      value: widthInput,
      update: setWidthInput,
      error: widthError,
      min: constraints.minWidthCm,
      max: constraints.maxWidthCm,
    },
    {
      id: "height",
      label: "גובה (ס״מ)",
      value: heightInput,
      update: setHeightInput,
      error: heightError,
      min: constraints.minHeightCm,
      max: constraints.maxHeightCm,
    },
    {
      id: "quantity",
      label: "כמות",
      value: quantityInput,
      update: setQuantityInput,
      error: quantityError,
      min: constraints.minQuantity,
      max: constraints.maxSelfServeQuantity,
    },
  ];
  return (
    <section
      className="hp-quick-order"
      id="quick-order"
      aria-labelledby="hp-quick-order-title"
    >
      <div className="hp-quick-order-inner hp-section">
        <div className="hp-quick-order-content">
          <span className="hp-eyebrow">
            <Icon name="custom" size={18} /> המידה שלכם. העיצוב שלכם.
          </span>
          <h2 id="hp-quick-order-title">שלט PVC בדיוק במידה שלכם</h2>
          <p>בוחרים מידה, רואים מחיר וממשיכים לעיצוב או להעלאת קובץ.</p>
          <form
            className="hp-quick-order-form"
            onSubmit={(event) => event.preventDefault()}
          >
            <div className="hp-quick-order-fields">
              {fields.map((field) => {
                const labelId = `hp-${field.id}-label`;
                const inputId = `hp-${field.id}-input`;
                const errorId = `hp-${field.id}-error`;
                return (
                  <label className="hp-field" htmlFor={inputId} key={field.id}>
                    <span id={labelId}>{field.label}</span>
                    <input
                      id={inputId}
                      type="number"
                      inputMode="numeric"
                      min={field.min}
                      max={field.max}
                      step={1}
                      value={field.value}
                      onChange={(event) => field.update(event.target.value)}
                      aria-invalid={Boolean(field.error)}
                      aria-labelledby={labelId}
                      aria-describedby={field.error ? errorId : undefined}
                    />
                    {field.error ? (
                      <small className="hp-field-error" id={errorId}>
                        {field.id === "quantity"
                          ? `כמות: ${field.min} עד ${field.max}, במספרים שלמים.`
                          : dimensionDescription}
                      </small>
                    ) : null}
                  </label>
                );
              })}
            </div>
          </form>
          <div
            className="hp-quick-order-price"
            aria-live="polite"
            aria-busy={loading}
          >
            {!isValid ? (
              <p>יש להזין מידה וכמות תקינות כדי לחשב מחיר.</p>
            ) : quote ? (
              <>
                <span>
                  סה״כ ל־{quantity} {quantity === 1 ? "יחידה" : "יחידות"}
                </span>
                <strong className="hp-quick-order-price-value">
                  <bdi>{formatMoney(quote.totalMinor, quote.currencyCode)}</bdi>
                </strong>
                <small>
                  {quote.includesVat ? "כולל מע״מ" : "לפני מע״מ"} · PVC בעובי 3
                  מ״מ · ללא תוספות ומשלוח
                </small>
                {readyDate && Number.isFinite(readyDate.getTime()) ? (
                  <small>
                    מועד הכנה משוער:{" "}
                    {new Intl.DateTimeFormat("he-IL", {
                      day: "numeric",
                      month: "long",
                    }).format(readyDate)}
                  </small>
                ) : null}
              </>
            ) : hasError ? (
              <>
                <p>
                  {state.status === "rejected"
                    ? "לא ניתן לתמחר את הבחירה הזו כרגע."
                    : "המחיר אינו זמין כרגע. אפשר לנסות שוב או להמשיך לעמוד המוצר."}
                </p>
                <button className="hp-text-button" onClick={retry}>
                  נסו שוב
                </button>
              </>
            ) : (
              <p className="hp-price-loading">
                <span className="hp-spinner" aria-hidden="true" /> מעדכנים מחיר…
              </p>
            )}
          </div>
          <div className="hp-quick-order-cta-row">
            {continueHref ? (
              <a className="hp-btn hp-btn-primary" href={continueHref}>
                לעיצוב ולהזמנה <span aria-hidden="true">←</span>
              </a>
            ) : (
              <button className="hp-btn hp-btn-primary" disabled>
                לעיצוב ולהזמנה
              </button>
            )}
            {continueHref ? (
              <a className="hp-text-link" href={continueHref}>
                יש לי קובץ מוכן
              </a>
            ) : null}
          </div>
        </div>
        <figure className="hp-dimension-preview">
          <div className="hp-sign-stage">
            <div className="hp-sign-bounds">
              <div className="hp-sign-outline" style={previewStyle}>
                <span className="hp-dimension-width">
                  <bdi>{isValid ? width : "—"}</bdi> ס״מ
                </span>
                <span className="hp-dimension-height">
                  <bdi>{isValid ? height : "—"}</bdi> ס״מ
                </span>
                {showPreviewCopy ? (
                  <span className="hp-preview-copy">
                    העיצוב שלכם<small>מתחיל כאן</small>
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <figcaption>תצוגת יחס המידות · העיצוב נעשה בשלב הבא</figcaption>
        </figure>
      </div>
    </section>
  );
}
